import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Language } from '../i18n/translations';

const LOGO_URL = '/logo.png';

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const langs: { code: Language; label: string }[] = [
    { code: 'pt', label: 'PT' },
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
  ];

  const navLinks = [
    { href: '/#services', label: t('nav.services'), testId: 'nav-services' },
    { href: '/#industries', label: t('nav.industries'), testId: 'nav-industries' },
    { href: '/#about', label: t('nav.about'), testId: 'nav-about' },
    { href: '/#contact-form', label: t('nav.contact'), testId: 'nav-contact' },
  ];

  const navLinkStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: scrolled ? 'var(--text-secondary)' : 'rgba(255,255,255,0.85)', letterSpacing: '0.06em', textTransform: 'uppercase', transition: 'color 0.2s' };

  return (
    <header data-testid="main-header" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
      background: scrolled ? 'rgba(255,255,255,0.96)' : 'rgba(10,17,40,0.3)',
      backdropFilter: 'blur(20px)', borderBottom: scrolled ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.08)',
      transition: 'all 0.3s',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }} data-testid="logo-link">
          <img src={LOGO_URL} alt="Maluz Technologies" style={{ height: 40 }} />
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em' }}>
            <span style={{ color: 'var(--secondary)' }}>MALUZ</span>{' '}
            <span style={{ color: scrolled ? 'var(--primary)' : 'white' }}>TECHNOLOGIES</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          {navLinks.map(link => (
            <a key={link.testId} href={link.href} data-testid={link.testId} style={navLinkStyle}
              onMouseEnter={e => (e.currentTarget.style.color = scrolled ? 'var(--primary)' : 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = scrolled ? 'var(--text-secondary)' : 'rgba(255,255,255,0.85)')}
            >{link.label}</a>
          ))}

          {/* Language Switcher */}
          <div style={{ position: 'relative' }}>
            <button data-testid="language-switcher" onClick={() => setLangOpen(!langOpen)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: scrolled ? 'var(--text-secondary)' : 'rgba(255,255,255,0.85)', background: 'none', border: '1px solid ' + (scrolled ? 'var(--border)' : 'rgba(255,255,255,0.25)'), padding: '5px 10px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
              <Globe size={13} /> {lang.toUpperCase()} <ChevronDown size={11} />
            </button>
            {langOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'white', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 80, zIndex: 100 }}>
                {langs.map(l => (
                  <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => { setLang(l.code); setLangOpen(false); }}
                    style={{ display: 'block', width: '100%', padding: '8px 16px', textAlign: 'left', fontSize: 13, fontWeight: lang === l.code ? 700 : 400, color: lang === l.code ? 'var(--primary)' : 'var(--text-secondary)', background: lang === l.code ? 'var(--bg-section)' : 'white', border: 'none', cursor: 'pointer', letterSpacing: '0.1em' }}
                  >{l.label}</button>
                ))}
              </div>
            )}
          </div>

          {/* Auth */}
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {user.role === 'admin' ? (
                <Link to="/admin" data-testid="nav-admin-link" style={{ fontSize: 13, fontWeight: 700, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('nav.admin')}</Link>
              ) : (
                <Link to="/portal" data-testid="nav-portal-link" style={{ fontSize: 13, fontWeight: 700, color: scrolled ? 'var(--primary)' : 'white', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('nav.portal')}</Link>
              )}
              <button onClick={handleLogout} data-testid="logout-btn" style={{ fontSize: 13, fontWeight: 500, color: scrolled ? 'var(--text-muted)' : 'rgba(255,255,255,0.6)', background: 'none', border: 'none', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer' }}>{t('nav.logout')}</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Link to="/login" data-testid="nav-login-link" style={{ fontSize: 13, fontWeight: 500, color: scrolled ? 'var(--text-secondary)' : 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('nav.login')}</Link>
              <Link to="/services" data-testid="nav-quote-link" style={{ background: 'var(--secondary)', color: 'white', padding: '9px 20px', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', transition: 'background 0.2s' }}>{t('nav.quote')}</Link>
            </div>
          )}
        </nav>

        {/* Mobile menu button */}
        <button className="mobile-menu-btn" data-testid="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)} style={{ display: 'none', background: 'none', border: 'none', color: scrolled ? 'var(--text-primary)' : 'white', cursor: 'pointer' }}>
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {menuOpen && (
        <div className="mobile-nav" style={{ background: 'white', borderTop: '1px solid var(--border)', padding: '20px 24px', maxHeight: '80vh', overflowY: 'auto' }}>
          {navLinks.map(link => (
            <a key={link.testId} href={link.href} onClick={() => setMenuOpen(false)} style={{ display: 'block', padding: '12px 0', fontSize: 16, fontWeight: 500, color: 'var(--text-primary)', borderBottom: '1px solid var(--bg-section)' }}>{link.label}</a>
          ))}
          <div style={{ display: 'flex', gap: 8, padding: '16px 0', borderBottom: '1px solid var(--bg-section)' }}>
            {langs.map(l => (
              <button key={l.code} onClick={() => { setLang(l.code); }}
                style={{ padding: '6px 14px', fontSize: 13, fontWeight: lang === l.code ? 700 : 400, color: lang === l.code ? 'white' : 'var(--text-secondary)', background: lang === l.code ? 'var(--primary)' : 'var(--bg-section)', border: '1px solid var(--border)', cursor: 'pointer' }}
              >{l.label}</button>
            ))}
          </div>
          <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {user ? (
              <>
                <Link to={user.role === 'admin' ? '/admin' : '/portal'} onClick={() => setMenuOpen(false)} style={{ padding: '12px 0', fontWeight: 600, color: 'var(--primary)' }}>{user.role === 'admin' ? t('nav.admin') : t('nav.portal')}</Link>
                <button onClick={() => { handleLogout(); setMenuOpen(false); }} style={{ textAlign: 'left', padding: '12px 0', fontWeight: 500, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>{t('nav.logout')}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} style={{ padding: '12px 0', fontWeight: 500, color: 'var(--text-primary)' }}>{t('nav.login')}</Link>
                <Link to="/services" onClick={() => setMenuOpen(false)} style={{ background: 'var(--secondary)', color: 'white', padding: '14px 24px', fontWeight: 600, textAlign: 'center', display: 'block' }}>{t('nav.quote')}</Link>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </header>
  );
}
